import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ExternalLink, AlertTriangle, CheckCircle, XCircle, Clock, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';

// Shared SEO Layout Component
export const SEOLayout = ({ children, title, description, keywords, breadcrumbs }) => {
  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords?.join(', ')} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <html lang="en" />
        <link rel="canonical" href={`https://petflycheck.io${window.location.pathname.replace(/\/$/, '') || '/'}`} />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="/logo.png" 
                alt="PetFlyCheck" 
                className="w-10 h-10 md:w-12 md:h-12 object-contain"
              />
              <span className="font-bold text-xl md:text-2xl text-slate-900">PetFlyCheck</span>
            </Link>
            <Link to="/">
              <Button variant="default" className="bg-blue-600 hover:bg-blue-700">
                Check Your Crate Now
              </Button>
            </Link>
          </div>
        </header>

        {/* Breadcrumbs */}
        {breadcrumbs && (
          <nav className="max-w-6xl mx-auto px-4 py-3 text-sm">
            <ol className="flex items-center gap-2 text-slate-600">
              <li><Link to="/" className="hover:text-blue-600">Home</Link></li>
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  <li>/</li>
                  <li>
                    {crumb.href ? (
                      <Link to={crumb.href} className="hover:text-blue-600">{crumb.label}</Link>
                    ) : (
                      <span className="text-slate-900">{crumb.label}</span>
                    )}
                  </li>
                </React.Fragment>
              ))}
            </ol>
          </nav>
        )}

        {/* Main Content */}
        <main className="max-w-6xl mx-auto px-4 py-8">
          {children}
        </main>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-blue-600 to-blue-700 py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Check Your Crate Before the Airport
            </h2>
            <p className="text-blue-100 text-lg mb-8">
              Enter your pet's measurements and upload 3 crate photos. We apply the IATA size formula
              and check what is visible: bolts, door, ventilation on four sides, bowls.
            </p>
            <Link to="/">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 text-lg px-8 py-6">
                Check My Crate - $39
              </Button>
            </Link>
            <p className="text-blue-100 text-xs mt-4">Independent service, not affiliated with IATA or any airline.</p>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 text-slate-400 py-12">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <img src="/logo.png" alt="PetFlyCheck" className="w-10 h-10 object-contain" />
                  <span className="text-white font-semibold text-lg">PetFlyCheck</span>
                </div>
                <p className="text-sm">AI-powered pet travel compliance pre-check. Know before you go.</p>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-4">Resources</h3>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/airlines" className="hover:text-white">Airline Policies</Link></li>
                  <li><Link to="/destinations" className="hover:text-white">Country Requirements</Link></li>
                  <li><Link to="/breeds" className="hover:text-white">Breed Guides</Link></li>
                  <li><Link to="/guides" className="hover:text-white">Travel Guides</Link></li>
                </ul>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-4">Company</h3>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/business" className="hover:text-white text-blue-400 font-medium">For Business / B2B</Link></li>
                  <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
                  <li><Link to="/support" className="hover:text-white">Support</Link></li>
                  <li><Link to="/terms" className="hover:text-white">Terms</Link></li>
                  <li><Link to="/privacy" className="hover:text-white">Privacy</Link></li>
                </ul>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-4">Get Checked</h3>
                <Link to="/">
                  <Button className="bg-blue-600 hover:bg-blue-700 w-full">
                    Start Now - $39
                  </Button>
                </Link>
              </div>
            </div>
            <div className="border-t border-slate-800 pt-8 text-sm text-center">
              <p>&copy; {new Date().getFullYear()} PetFlyCheck. All rights reserved.</p>
              <p className="mt-2">Independent service, not affiliated with IATA or any airline. This is a pre-check, not an airline approval. Always confirm with your airline.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

// Alert Box Component
export const AlertBox = ({ type = 'warning', title, children }) => {
  const styles = {
    warning: 'bg-amber-50 border-amber-300 text-amber-900',
    danger: 'bg-red-50 border-red-300 text-red-900',
    success: 'bg-green-50 border-green-300 text-green-900',
    info: 'bg-blue-50 border-blue-300 text-blue-900'
  };
  
  const icons = {
    warning: <AlertTriangle className="w-5 h-5 text-amber-600" />,
    danger: <XCircle className="w-5 h-5 text-red-600" />,
    success: <CheckCircle className="w-5 h-5 text-green-600" />,
    info: <FileText className="w-5 h-5 text-blue-600" />
  };

  return (
    <div className={`border-2 rounded-lg p-4 ${styles[type]}`}>
      <div className="flex items-start gap-3">
        {icons[type]}
        <div>
          {title && <h4 className="font-semibold mb-1">{title}</h4>}
          <div className="text-sm">{children}</div>
        </div>
      </div>
    </div>
  );
};

// Info Card Component
export const InfoCard = ({ title, items, icon }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
    <div className="flex items-center gap-3 mb-4">
      {icon}
      <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
    </div>
    <ul className="space-y-2">
      {items.map((item, index) => (
        <li key={index} className="flex items-start gap-2 text-slate-700">
          <span className="text-blue-600 mt-1">•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

// Stat Card Component
export const StatCard = ({ value, label, sublabel, type = 'neutral' }) => {
  const colors = {
    danger: 'text-red-600',
    warning: 'text-amber-600',
    success: 'text-green-600',
    neutral: 'text-slate-900'
  };
  
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
      <div className={`text-3xl font-bold ${colors[type]}`}>{value}</div>
      <div className="text-slate-600 font-medium mt-1">{label}</div>
      {sublabel && <div className="text-slate-500 text-sm mt-1">{sublabel}</div>}
    </div>
  );
};

export default SEOLayout;
